import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";
import { Save, Palette } from "lucide-react";

const LOGOS = [
  { key: "logo_light", label: "Header logo (light background)" },
  { key: "logo_dark", label: "Header logo (dark background)" },
  { key: "logo_mobile", label: "Mobile logo" },
  { key: "logo_footer", label: "Footer logo" },
  { key: "logo_admin", label: "Admin portal logo" },
  { key: "logo_email", label: "Email logo" },
  { key: "favicon", label: "Favicon (ICO or PNG 32×32)" },
  { key: "apple_touch_icon", label: "Apple touch icon (PNG 180×180)" },
  { key: "social_share_image", label: "Default social share image (1200×630)" },
  { key: "default_banner", label: "Default page banner" },
];

export default function AdminBranding() {
  const { refresh } = useSettings();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/settings").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="p-8">Loading...</div>;

  const branding = data.branding || {};

  const setLogo = (key, value) => {
    setData({ ...data, branding: { ...(data.branding || {}), [key]: value } });
  };

  const upload = async (key, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: res } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Use backend proxy URL as absolute
      const url = res.url.startsWith("http") ? res.url : `${process.env.REACT_APP_BACKEND_URL}${res.url}`;
      setLogo(key, url);
      toast.success("Uploaded — remember to Save");
    } catch (e) { toast.error("Upload failed"); }
  };

  const save = async () => { try { await api.put("/settings", { data }); toast.success("Saved"); refresh(); } catch { toast.error("Save failed"); } };

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-neutral-950 flex items-center justify-center"><Palette className="w-5 h-5 text-sky-400" /></div>
            <h1 className="font-display font-black text-3xl" data-testid="admin-branding-title">Branding & Media</h1>
          </div>
          <p className="text-neutral-500 mt-2">Upload or paste URLs for every logo variant. Changes reflect on the site after saving.</p>
        </div>
        <button onClick={save} className="btn-primary-eq text-sm" data-testid="branding-save"><Save className="w-4 h-4" /> Save</button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {LOGOS.map((l) => (
          <div key={l.key} className="bg-white rounded-2xl p-5 border border-neutral-200" data-testid={`branding-${l.key}`}>
            <div className="text-sm font-semibold text-neutral-800">{l.label}</div>
            <div className="mt-3 aspect-video rounded-xl bg-neutral-100 border border-neutral-200 flex items-center justify-center overflow-hidden">
              {branding[l.key] ? (
                <img src={branding[l.key]} alt={l.label} className="max-w-full max-h-full object-contain p-4" />
              ) : (
                <div className="text-xs text-neutral-400">No image set</div>
              )}
            </div>
            <input value={branding[l.key] || ""} onChange={(e) => setLogo(l.key, e.target.value)}
                   placeholder="Paste image URL or upload below"
                   className="w-full mt-3 px-3 py-2 border border-neutral-200 rounded-lg text-xs" />
            <label className="mt-2 btn-outline-eq w-full justify-center text-xs cursor-pointer py-2">
              Upload file
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon,image/vnd.microsoft.icon" hidden onChange={(e) => e.target.files?.[0] && upload(l.key, e.target.files[0])} />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
